// Placement Decision Auditor — shared, READ-ONLY "Why it failed" dialog.
// Renders the ordered stuffing-rule decision tree returned by
// POST /api/container-planner/audit-placement. Pure presentation: it never changes a plan.
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, AlertTriangle, HelpCircle, SlidersHorizontal, Wrench, Loader2 } from "lucide-react";

const STEP_ICON = {
  pass: { Icon: CheckCircle2, cls: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50" },
  fail: { Icon: XCircle, cls: "text-rose-600", ring: "border-rose-300 bg-rose-50" },
  warn: { Icon: AlertTriangle, cls: "text-amber-600", ring: "border-amber-200 bg-amber-50" },
  skip: { Icon: HelpCircle, cls: "text-slate-300", ring: "border-slate-200 bg-slate-50" },
};

export function PlacementAuditor({ data, onClose, onFixIt, colorOf }) {
  const open = !!data;
  const loading = !!data?.loading;
  const rules = data?.rules || [];
  const blocked = data?.blocked_step || null;
  const cand = data?.candidate || {};
  const place = data?.placement || {};
  const fixIt = data?.fix_it || null;
  const swatch = (colorOf && cand.product_code) ? colorOf(cand.product_code) : "#64748b";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white text-slate-900 max-w-lg max-h-[85vh] overflow-auto" data-testid="placement-auditor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-blue-700" />
            {loading ? "Analysing placement…" : (blocked ? "Why this pallet was blocked" : "Placement audit")}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Read-only check of every stuffing rule, in order. Nothing was changed in the plan.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex flex-col items-center justify-center gap-3 text-slate-500" data-testid="placement-auditor-loading">
            <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
            <div className="text-sm">Running a fresh audit for <b className="text-slate-800">{cand.product_code || "this pallet"}</b>…</div>
          </div>
        ) : (
        <>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 mb-1" data-testid="placement-auditor-summary">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3.5 h-3.5 rounded-sm shrink-0" style={{ background: swatch }} />
            <span className="font-semibold text-sm">{cand.product_code}</span>
            {cand.description ? <span className="text-[11px] text-slate-500 truncate">{cand.description}</span> : null}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Adding <b>1 pallet</b>{cand.pallet_weight_kg ? <> · <b>{Math.round(cand.pallet_weight_kg).toLocaleString()} kg</b></> : null}
            {place.summary ? <> · {place.summary}{place.lane ? ` (${place.lane === "B" ? "Right" : "Left"} Lane)` : ""}</> : null}
          </div>
          {blocked ? (
            <div className="mt-2 text-[12px] font-semibold text-rose-700 flex items-center gap-1.5" data-testid="placement-auditor-verdict">
              <XCircle className="w-4 h-4" />Blocked at Step {blocked}: {(rules.find((r) => r.step === blocked) || {}).name}
            </div>
          ) : (
            <div className="mt-2 text-[12px] font-semibold text-emerald-700 flex items-center gap-1.5" data-testid="placement-auditor-verdict">
              <CheckCircle2 className="w-4 h-4" />All rules pass — this pallet can be placed here.
            </div>
          )}
        </div>

        <ol className="space-y-1.5" data-testid="placement-auditor-tree">
          {rules.map((r) => {
            const reached = r.reached !== false;
            const st = reached ? (STEP_ICON[r.status] || STEP_ICON.skip) : STEP_ICON.skip;
            const isBlock = r.step === blocked;
            const Icon = reached ? st.Icon : HelpCircle;
            return (
              <li key={r.step}
                className={`rounded-lg border px-3 py-2 ${isBlock ? "border-rose-400 bg-rose-50 ring-1 ring-rose-200" : reached ? st.ring : "border-dashed border-slate-200 bg-slate-50 opacity-60"}`}
                data-testid={`audit-rule-${r.step}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={`w-4 h-4 shrink-0 ${reached ? st.cls : "text-slate-300"}`} />
                    <span className="text-[10px] font-mono text-slate-400 shrink-0">{r.step}</span>
                    <span className="text-[13px] font-medium truncate">{r.name}</span>
                  </div>
                  <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded shrink-0 ${
                    !reached ? "bg-slate-200 text-slate-400"
                      : r.status === "pass" ? "bg-emerald-100 text-emerald-700"
                        : r.status === "fail" ? "bg-rose-200 text-rose-800"
                          : "bg-amber-100 text-amber-700"}`} data-testid={`audit-rule-status-${r.step}`}>
                    {reached ? r.status.toUpperCase() : "NOT REACHED"}
                  </span>
                </div>
                {reached && (
                  <div className="mt-1 pl-6 text-[11px] text-slate-600">
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 font-mono">
                      <span data-testid={`audit-rule-actual-${r.step}`}>Actual: <b className={isBlock ? "text-rose-700" : "text-slate-800"}>{r.actual}</b></span>
                      <span data-testid={`audit-rule-limit-${r.step}`}>Limit: <b className="text-slate-800">{r.limit}</b></span>
                    </div>
                    {r.detail ? <div className="mt-0.5 text-slate-500">{r.detail}</div> : null}
                  </div>
                )}
                {isBlock && <div className="mt-1 pl-6 text-[10px] font-bold uppercase tracking-wider text-rose-600" data-testid={`audit-blocked-here-${r.step}`}>⛔ Blocked here</div>}
              </li>
            );
          })}
        </ol>

        {fixIt && (
          <div className="mt-2 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2.5" data-testid="placement-auditor-fixit">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-800 mb-1">
              <Wrench className="w-3.5 h-3.5" />Fix-it recommendation
            </div>
            <div className="text-[12px] text-slate-700" data-testid="placement-auditor-fixit-text">{fixIt.text}</div>
            {fixIt.action && onFixIt && (
              <Button size="sm" className="mt-2 h-8 bg-blue-600 hover:bg-blue-500 text-white" onClick={() => onFixIt(fixIt.action)} data-testid="placement-auditor-fixit-action">
                {fixIt.action.label || "Apply fix"}
              </Button>
            )}
          </div>
        )}
        </>
        )}

        <DialogFooter>
          <Button variant="outline" className="bg-transparent" onClick={onClose} data-testid="placement-auditor-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
